import type { QueryKey } from '@tanstack/react-query';
import {
  apiListQuery,
  apiQuery,
  createQueryKeys,
  useApiMutation,
  type ApiMutationOptions,
} from '@/shared/data';
import { getCurrentTenant } from '@/shared/http/client';
import { getSignedUploadUrl, putFileToSignedUrl } from '@/shared/reference';
import {
  assignCreditProduct,
  cloneCreditProduct,
  createSignatureTemplate,
  deleteCreditProduct,
  getAvailableWallets,
  getCreditProduct,
  listCreditProductOptions,
  listCreditProducts,
  listRuleFields,
  saveCreditProduct,
  shareCreditProduct,
  type CreditProductListParams,
} from './credit-product.api';
import type { AssignBody, ShareBody, TemplateBody } from './credit-product.wire';
export const creditProductKeys = createQueryKeys('credit-product');
export function creditProductOptionsQuery(
  params: { searchString?: string | undefined; tenant?: string | undefined } = {},
  extra: { enabled?: boolean } = {},
) {
  const resolved = { ...params, tenant: 'tenant' in params ? params.tenant : getCurrentTenant() };
  return apiListQuery(
    creditProductKeys.custom('options', resolved),
    ({ signal }) => listCreditProductOptions(resolved, signal),
    extra,
  );
}

/** Consulta de lista; o consumidor mantém o gate CreditProduct:Read do legado. */
export function creditProductsQuery(
  params: CreditProductListParams = {},
  extra: { enabled?: boolean } = {},
) {
  const resolved = { ...params, tenant: 'tenant' in params ? params.tenant : getCurrentTenant() };
  return apiListQuery(
    creditProductKeys.list(resolved),
    ({ signal }) => listCreditProducts(resolved, signal),
    extra,
  );
}

/** `useCreditProductData` (`creditProductContext.tsx:36-98`): GET com o tenant corrente. */
export function creditProductDetailQuery(id: string) {
  const tenant = getCurrentTenant();
  return apiQuery(
    creditProductKeys.custom('detail', id, tenant),
    ({ signal }) => getCreditProduct(id, tenant, signal),
    { enabled: id !== 'novo' && id !== '' },
  );
}

export function availableWalletsQuery(params: {
  categoryName?: string | undefined;
  liquidationApprovalTenantName?: string | undefined;
  productId?: string | undefined;
}) {
  return apiQuery(
    creditProductKeys.custom('available-wallets', params),
    ({ signal }) => getAvailableWallets(params, signal),
    { enabled: !!params.categoryName },
  );
}

export function ruleFieldsQuery(enabled: boolean) {
  return apiQuery(creditProductKeys.custom('rule-fields'), ({ signal }) => listRuleFields(signal), {
    enabled,
    staleTime: 30 * 60_000,
  });
}

const def = <V, D>(
  run: (variables: V) => Promise<D>,
  invalidate: readonly QueryKey[] = [creditProductKeys.all],
) => ({ run, invalidate });

export const creditProductMutations = {
  save: def(({ id, payload }: { id: string | undefined; payload: Record<string, unknown> }) =>
    saveCreditProduct(id, payload, getCurrentTenant()),
  ),
  clone: def(({ payload, tenant }: { payload: Record<string, unknown>; tenant: string }) =>
    cloneCreditProduct(payload, tenant),
  ),
  delete: def(({ id }: { id: string }) => deleteCreditProduct(id)),
  assign: def(({ id, body }: { id: string; body: AssignBody }) => assignCreditProduct(id, body)),
  share: def(({ id, body }: { id: string; body: ShareBody }) =>
    shareCreditProduct(id, body, getCurrentTenant()),
  ),
  template: def(
    ({ productId, body }: { productId: string; body: TemplateBody }) =>
      createSignatureTemplate(productId, body),
    [],
  ),
  upload: def(
    async ({
      file,
      onProgress,
      signal,
    }: {
      file: File;
      onProgress?: ((fraction: number) => void) | undefined;
      signal?: AbortSignal | undefined;
    }) => {
      const signed = await getSignedUploadUrl(file.name, signal);
      await putFileToSignedUrl(file, signed.putUrl, {
        ...(onProgress ? { onProgress } : {}),
        ...(signal ? { signal } : {}),
      });
      return signed;
    },
    [],
  ),
};

type Name = keyof typeof creditProductMutations;
type Variables<K extends Name> = Parameters<(typeof creditProductMutations)[K]['run']>[0];
type Data<K extends Name> = Awaited<ReturnType<(typeof creditProductMutations)[K]['run']>>;

/** Um hook por módulo, tipado pelo nome (X07); o feedback é da tela (X06). */
export function useCreditProductMutation<K extends Name>(
  name: K,
  options: Omit<ApiMutationOptions<Data<K>, Variables<K>>, 'mutationFn'> = {},
) {
  const entry = creditProductMutations[name] as unknown as {
    run: (variables: Variables<K>) => Promise<Data<K>>;
    invalidate: readonly QueryKey[];
  };
  return useApiMutation<Data<K>, Variables<K>>({
    mutationKey: [...creditProductKeys.all, 'mutation', name],
    success: false,
    error: false,
    invalidate: entry.invalidate,
    ...options,
    mutationFn: entry.run,
  });
}
